// Each group has `core` (filled chips — "hire me for this") and `familiar`
// (outline chips — "used it, can talk about it"). A `muted` group renders
// de-emphasized at the bottom.
export const skillCategories = [
  {
    title: "Programming Languages",
    core: ["JavaScript", "TypeScript", "Python", "Swift"],
    familiar: ["Java", "C#", "C++"]
  },
  {
    title: "Frontend",
    core: ["React", "React Native", "HTML", "CSS", "Tailwind CSS", "Material-UI"],
    familiar: []
  },
  {
    title: "Backend & Databases",
    core: ["Python (Flask)", "SQL", "Neo4j"],
    familiar: []
  },
  {
    title: "Salesforce",
    core: ["Lightning Web Components", "Agentforce", "Salesforce Certified: Platform Foundations"],
    familiar: []
  },
  {
    title: "UX & Design",
    core: ["Figma", "Photoshop", "Affinity"],
    familiar: []
  },
  {
    title: "Tools",
    core: ["Git / GitHub", "Agile / Scrum"],
    familiar: []
  },
  {
    title: "Exploring",
    muted: true,
    core: [],
    familiar: ["Blender", "Substance Painter", "Unity", "ComfyUI (Hunyuan 3D)", "LangChain", "Ollama", "ESP32"]
  }
];

export const itProjects = [
  {
    title: "Bluetooth Scanning IoT Device",
    description: [
      "During the IoT course, I gained valuable hands-on experience working with embedded systems. My teammate and I designed a Bluetooth scanning device that activates when the user clicks a button. Once triggered, the ESP32 scans for Bluetooth devices in the surrounding environment and displays the results on an LCD screen. Additionally, a speaker with an amplifier announces the results in Morse code.",
      "This project provided an excellent hands-on experience that taught me embedded systems programming and IoT fundamentals. It also strengthened my teamwork and communication skills as we collaborated to integrate both hardware and software components."
    ],
    tech: ["C++ (Arduino)", "Embedded Systems", "ESP32", "LCD Display", "Speaker Amplifier", "Team Collaboration"],
    github: "https://github.com/qian-27/Bluetooth-Scanning",
    linkedin: "https://www.linkedin.com/feed/update/urn:li:activity:7383896434431008768/"
  },
  {
    title: "Ryhti Info (iOS App)",
    description: [
      "I developed an iOS application with clean UX design that utilizes open construction data from both the Ryhti project and the City of Helsinki. Using both APIs, I retrieved detailed building information such as the number of floors, area size, room count, and elevator availability.",
      "This project gave me hands-on experience with Swift programming and Apple design principles. It taught me mobile-focused UX design fundamentals and how to optimize performance for native iOS development."
    ],
    tech: ["Swift", "iOS Development", "Mobile UX Design", "Xcode"],
    github: "https://github.com/qian-27/Ryhti_Info"
  },
  {
    title: "Unity Racing Game",
    description: [
      "During a Unity course, I gained hands-on experience building interactive 3D games and working with physics in a real-time environment. As my final project, I developed a small 3D racing game that challenged me to design responsive controls, realistic movement, and interactive environments.",
      "This project strengthened my problem-solving skills and gave me practical experience with Unity, C#, and core game development concepts, while pushing me to think both creatively and technically from start to finish."
    ],
    tech: ["C#", "Unity", "3D Game Development", "Physics Simulation"],
    linkedin: "https://www.linkedin.com/feed/update/urn:li:activity:7289668121706070018/"
  },
  {
    title: "AI 3D Model Generator Workflow",
    description: [
      "I developed a local, cost-free workflow for generating 3D assets for VR applications using ComfyUI and Tencent Hunyuan 3D, allowing me to quickly turn ideas into models. The process starts with a simple visual prompt or hand-drawn sketch, which is converted into a 3D model through an AI-driven pipeline. The generated assets can then be refined in Blender or imported directly into Unity for testing.",
      "This workflow enables fast experimentation, efficient iteration, and creative exploration, helping me quickly evaluate concepts and streamline the early stages of VR development."
    ],
    tech: ["AI Workflow Design", "ComfyUI", "Unity", "Blender", "VR Development", "Tencent Hunyuan 3D"],
    linkedin: "https://www.linkedin.com/feed/update/urn:li:activity:7292167109151764482/"
  },
  {
    title: "React Native Movie App",
    description: "I developed a mobile application for browsing and searching movies using real-time API data, allowing users to explore detailed movie information through a clean and intuitive interface. This project gave me hands-on experience integrating external APIs, managing application state effectively, and building native-like experiences for mobile platforms. It also helped me better understand the unique considerations and nuances of mobile development compared to web-based applications.",
    tech: ["React Native", "API Integration", "State Management", "Mobile UI Development"],
    github: "https://github.com/qian-27/React-Native-Movie-App"
  },
  {
    title: "React Car App",
    description: "I developed this project by integrating APIs with Material-UI to create a polished and user-friendly car listing interface. Through this work, I strengthened my ability to fetch, filter, and present dynamic data efficiently. I also improved my understanding of accessibility best practices and UI consistency by designing reusable components. Working with Material-UI’s component library helped me gain a deeper understanding of design systems and scalable front-end development.",
    tech: ["React", "Material-UI", "API Integration", "Data Filtering"],
    github: "https://github.com/qian-27/React-Car-App"
  },
  {
    title: "Python Game Project",
    description: "In this project, I designed and developed a 2D game fully in Python using Pygame as a final project for a 2022 programming MOOC. I implemented all core gameplay mechanics from scratch, including player movement, enemy logic, collision detection, scoring, and game state control. This project demonstrates my ability to build a complete interactive system, structure code effectively, and apply solid programming fundamentals to a real project.",
    tech: ["Python", "Game Logic Development", "Problem Solving", "Object-Oriented Programming"],
    github: "https://github.com/qian-27/Python-Own-Game"
  },
    {
    title: "Collaboration Website (HH UAS × Indiana University)",
    description: "In this project, I served as team leader and frontend developer for an international student collaboration between Helsinki Haaga-Helia University of Applied Sciences and Indiana University. I coordinated communication across time zones and cultures and implemented website interface features using HTML, CSS, and JavaScript. This experience strengthened my leadership and teamwork skills and helped me better understand cross-cultural collaboration in a real project setting.",
    tech: ["HTML, CSS, JavaScript", "Team Management", "Frontend Development", "Cross-Cultural Collaboration"],
    github: "https://github.com/qian-27/Collaboration-Website"
  }
];

export const uxProjects = [
    {
    title: "Salesforce Lightning Web Component UI",
    description: [
      "I designed this concept for a Salesforce Lightning Web Component interface, focusing on structured layouts and efficient user workflows for enterprise environments. Through this project, I learned to consider business logic and process automation while designing UI components.",
      "It taught me how enterprise software design differs from consumer applications and the importance of workflow optimization."
    ],
    figmaLink: "https://www.figma.com/proto/fzJpul6bRDNf8UdD2jmvJp/Salesforce-LWC?node-id=1-4113&p=f&t=s1ScWP647dDhV3lU-1&scaling=min-zoom&content-scaling=fixed&page-id=0%3A1",
    image: "/images/salesforce-lwc.jpg",
    tags: ["Salesforce Platform", "Lightning Web Components", "UI/UX Design", "Enterprise Software Design"]
  },
  {
    title: "Moodle Redesign",
    description: [
      "In the prototyping course, we focused on e-learning research. Our team decided to redesign the Moodle platform at Haaga-Helia for a multi-subject course, enabling students from different backgrounds (designers, programmers, and business professionals) to join the same course and complete projects together.",
      "We focused on serving students who struggled with the platform's navigation, those who found it difficult to locate learning materials or check assignment grades. Through customer research with students at our school, we improved clarity and reduced friction in the course navigation by restructuring the menu and improving overall accessibility.",
      "This project demonstrates our ability to rethink user journeys from a user-centered perspective and design practical solutions that address real user pain points."
    ],
    figmaLink: "https://www.figma.com/proto/kg8YI4n8yu47xVSRP2oEHl/Moodle?pageid=0%3A1&node-id=1-1328&p=f&viewport=76%2C45%2C0.11&t=CVyZ8ggvlLb2ID3J-1&scaling=minzoom&content-scaling=fixed",
    image: "/images/moodle-redesign.jpg",
    tags: ["UX Design", "Navigation Design", "Accessibility", "User Research"]
  },
    {
    title: "Web Design Concept",
    description: "I created this modern website design prototype focusing on strong visual hierarchy and intuitive user interaction. I designed the layout to be scalable and adaptable for future content expansion. This project helped me explore design principles and practice responsive UI planning in Figma. It was a great exercise in thinking about design systems and how to create flexible, maintainable layouts.",
    figmaLink: "https://www.figma.com/proto/sZsND4BRM35Mzfc8VVpRHd/WebsiteDesign?page-id=0%3A1&node-id=1-4396&p=f&viewport=54%2C977%2C0.12&t=bOZTJZu8bdQa9qm6-1&scaling=min-zoom&content-scaling=fixed",
    image: "/images/web-design.jpg",
    tags: ["User Personas", "Web Design", "Visual Hierarchy", "Responsive Design", "Figma Prototyping"]
  },
  {
    title: "Grenear (Junction Hackathon 2023)",
    description: [
      "I participated in Junction 2023 and helped create this sustainability-focused prototype. I supported UX design decisions, contributed to UI implementation, and helped prepare video presentation materials.",
      "This hackathon taught me to work efficiently under tight deadlines while collaborating with my team. It also improved my ability to communicate with my team members and present ideas effectively to potential users."
    ],
    figmaLink: "https://www.figma.com/proto/uYJdj8t5AOJLs0ujJCdjA4/Energy-Now?node-id=55-6297&p=f&t=lio5wyxIUWLdeFK8-1&scaling=min-zoom&content-scaling=fixed&page-id=29%3A48",
    github: "https://github.com/qian-27/grenear",
    demo: "https://www.youtube.com/watch?v=12va3l8n3vw",
    image: "/images/grenear.jpg",
    tags: ["UX Design", "UI Implementation", "Hackathon Experience", "Rapid Prototyping", "Team Collaboration"]
  }
];
